import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AccountCreationForm } from '../../components/forms/AccountCreationForm';
import { AccountCreationReviewModal } from '../../components/forms/AccountCreationReviewModal';
import { ErrorOverlay } from '../../components/ui/ErrorOverlay';
import {
  fetchAccountCreationSubmission,
  getAcraAttachmentSignedUrl,
  normalizeAcraAttachments,
  normalizeSupportingAttachments,
  updateSubmissionStatus,
  type FinanceReviewAction,
} from '../../lib/applicationFormSubmissions';
import {
  fetchAccountCreationChangeLogs,
  insertAccountCreationChangeLog,
  type AccountCreationChangeLogRow,
} from '../../lib/accountCreationChangeLog';
import {
  formatSubmittedAt,
  PAYMENT_TERMS_OPTIONS,
  statusClass,
  STATUS_LABEL,
} from '../../lib/accountCreationUi';
import { formatSubmittedBy } from '../../lib/applicationFormSubmitter';
import { useFormWorkflowPermissions } from '../../hooks/useFormWorkflowPermissions';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { usePortal } from '../../contexts/PortalContext';
import { useAuth } from '../../contexts/AuthContext';
import type { FinanceReviewConfirmInput } from '../../components/forms/AccountCreationReviewModal';
import {
  CUSTOMER_PRICE_GROUP_OPTIONS,
} from '../../lib/accountCreationFinanceOptions';
import { useAccountCreationFieldOptions } from '../../lib/accountCreationFieldOptions';
import {
  lookupDeliveryScheduleByPostal,
  postalPrefixFromCode,
} from '../../lib/applicationFormSettings';
import {
  DEFAULT_ACCOUNT_CREATION_FINANCE,
  EMPTY_ACCOUNT_CREATION_FORM,
  type AccountCreationFinanceData,
  type AccountCreationFormData,
  type ApplicationSubmission,
} from '../../types/applicationForm';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import { emailToUsername } from '../../lib/emailUtils';

function Field({
  label,
  required,
  full,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label
      className={`af-form__field${full ? ' af-form__field--full' : ''}${className ? ` ${className}` : ''}`}
    >
      <span>
        {label}
        {required ? <span className="af-form__req">*</span> : null}
      </span>
      {children}
    </label>
  );
}

function AttachmentLinks({
  items,
  emptyLabel,
  onError,
}: {
  items: { path: string; name: string }[];
  emptyLabel: string;
  onError?: (message: string) => void;
}) {
  if (items.length === 0) {
    return <p className="af-attachments-panel__empty">{emptyLabel}</p>;
  }
  return (
    <ul className="af-attachments-panel__list">
      {items.map((item) => (
        <li key={item.path}>
          <button
            type="button"
            className="af-attachments-panel__link"
            onClick={() => {
              void (async () => {
                try {
                  const url = await getAcraAttachmentSignedUrl(item.path);
                  if (!url) {
                    onError?.('Attachment not found.');
                    return;
                  }
                  window.open(url, '_blank', 'noopener,noreferrer');
                } catch (err) {
                  onError?.(
                    err instanceof Error ? err.message : 'Failed to open attachment.',
                  );
                }
              })();
            }}
          >
            {item.name}
          </button>
        </li>
      ))}
    </ul>
  );
}

function FinanceSelect({
  value,
  options,
  placeholder,
  onChange,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function AccountCreationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { basePath } = usePortal();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const { canApprove, canVerify, loading: permissionsLoading } = useFormWorkflowPermissions();
  const { options: fieldOptions } = useAccountCreationFieldOptions();
  const listPath = basePath ? `${basePath}/account-creation` : '/account-creation';

  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<FinanceReviewAction | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [formDraft, setFormDraft] = useState<AccountCreationFormData>(EMPTY_ACCOUNT_CREATION_FORM);
  const [customerCode, setCustomerCode] = useState('');
  const [finance, setFinance] = useState<AccountCreationFinanceData>(DEFAULT_ACCOUNT_CREATION_FINANCE);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [routeHint, setRouteHint] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [changeLogs, setChangeLogs] = useState<AccountCreationChangeLogRow[]>([]);

  const actorLabel = () =>
    formatDisplayPersonName(profile?.display_name || profile?.username) ||
    formatDisplayPersonName(emailToUsername(authUser?.email ?? '')) ||
    authUser?.email ||
    'User';

  const loadChangeLogs = useCallback(async (submissionId: string) => {
    try {
      setChangeLogs(await fetchAccountCreationChangeLogs(submissionId));
    } catch {
      setChangeLogs([]);
    }
  }, []);

  const applySubmission = useCallback(
    (data: ApplicationSubmission) => {
      const form = data.form_data as AccountCreationFormData;
      setRow(data);
      setCompanyName(data.company_name);
      setFormDraft({ ...EMPTY_ACCOUNT_CREATION_FORM, ...form });
      setCustomerCode(data.customer_code?.trim() || '');
      setFinance({
        ...DEFAULT_ACCOUNT_CREATION_FINANCE,
        ...data.finance_data,
        salesperson_code:
          data.finance_data?.salesperson_code?.trim() ||
          form.salesperson_code?.trim() ||
          '',
        cut_off_time: data.finance_data?.cut_off_time?.trim() || '12:00 PM',
        division_code: '10',
        session: data.finance_data?.session?.trim() || 'AM',
      });
      setVerifyError(null);
      setEditing(false);
      // Remount form so initial* props re-apply after DB / realtime reload.
      setFormKey((k) => k + 1);
    },
    [],
  );

  const load = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!id) return;
      if (!opts?.quiet) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await fetchAccountCreationSubmission(id);
        if (!data) {
          setError('Application not found.');
          setRow(null);
          return;
        }
        applySubmission(data);
        await loadChangeLogs(data.id);
      } catch (err) {
        if (!opts?.quiet) {
          setError(err instanceof Error ? err.message : 'Failed to load application.');
        }
      } finally {
        if (!opts?.quiet) setLoading(false);
      }
    },
    [id, loadChangeLogs, applySubmission],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['account_creation_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load({ quiet: true });
    },
  });

  // Change log is a separate table — also listen so Verified / Approved rows appear live.
  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['account_creation_change_log'],
    filter: id ? `submission_id=eq.${id}` : undefined,
    onChange: () => {
      if (id) void loadChangeLogs(id);
    },
  });

  const verifying = Boolean(row && row.status === 'pending_review' && canVerify);
  const approving = Boolean(row && row.status === 'pending_approval' && canApprove);
  const canEditApplicant = verifying || approving;

  useEffect(() => {
    if (!verifying || !row) return;
    const form = row.form_data as AccountCreationFormData;
    const postal = form.delivery_postal?.trim() || '';
    if (!postal) {
      setRouteHint(null);
      return;
    }
    let cancelled = false;
    const prefix = postalPrefixFromCode(postal);
    void (async () => {
      try {
        const match = await lookupDeliveryScheduleByPostal(postal);
        if (cancelled || !match) return;
        setFinance((prev) => ({
          ...prev,
          territory_code: prev.territory_code.trim() || match.territory,
        }));
        const parts = [
          `Guideline for postal ${prefix}: schedule ${match.schedule || '—'} · ${match.territory || '—'}`,
        ];
        if (match.remarks.trim()) parts.push(match.remarks.trim());
        setRouteHint(parts.join('\n'));
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verifying, row]);

  const patchFinance = <K extends keyof AccountCreationFinanceData>(
    key: K,
    value: AccountCreationFinanceData[K],
  ) => {
    setFinance((prev) => ({ ...prev, [key]: value }));
  };

  const confirmReview = async (input: FinanceReviewConfirmInput) => {
    if (!row || !reviewAction) return;
    setReviewBusy(true);
    try {
      await updateSubmissionStatus(row.id, reviewAction, input, {
        userId: authUser?.id,
        username: profile?.username,
      });
      try {
        if (reviewAction === 'approved') {
          await insertAccountCreationChangeLog({
            submissionId: row.id,
            changedBy: actorLabel(),
            changeType: 'approved',
            summary: 'Approved by AR',
            afterJson: {
              company_name: input.companyName,
              form_data: input.formData,
              finance_data: input.financeData,
            },
          });
        } else if (reviewAction === 'rejected') {
          await insertAccountCreationChangeLog({
            submissionId: row.id,
            changedBy: actorLabel(),
            changeType: 'rejected',
            summary: `Rejected: ${input.reviewNotes?.trim() || '—'}`,
          });
        }
      } catch {
        /* non-blocking */
      }
      setReviewAction(null);
      await load();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleConfirmVerification = async () => {
    if (!row) return;
    setVerifyError(null);
    if (!companyName.trim()) {
      setVerifyError('Company name is required.');
      return;
    }
    if (!customerCode.trim()) {
      setVerifyError('Customer code is required.');
      return;
    }
    if (!finance.businessgroup_code.trim()) {
      setVerifyError('Business group code is required.');
      return;
    }
    if (!finance.customer_price_group.trim()) {
      setVerifyError('Customer price group is required.');
      return;
    }
    if (!finance.delivery_schedule.trim()) {
      setVerifyError('Delivery schedule is required.');
      return;
    }
    if (!finance.cut_off_time.trim()) {
      setVerifyError('Cut off time is required.');
      return;
    }
    if (!finance.session.trim()) {
      setVerifyError('Session is required.');
      return;
    }
    if (!finance.territory_code.trim()) {
      setVerifyError('Territory code is required.');
      return;
    }

    setReviewBusy(true);
    try {
      const before = {
        company_name: row.company_name,
        form_data: row.form_data,
        finance_data: row.finance_data ?? null,
        customer_code: row.customer_code ?? null,
      };
      const financePayload = {
        ...finance,
        division_code: '10',
        salesperson_code:
          formDraft.salesperson_code?.trim() || finance.salesperson_code.trim(),
      };
      const after = {
        company_name: companyName.trim(),
        form_data: formDraft,
        finance_data: financePayload,
        customer_code: customerCode.trim(),
      };
      await updateSubmissionStatus(
        row.id,
        'verified',
        {
          customerCode: customerCode.trim(),
          companyName: companyName.trim(),
          formData: formDraft,
          financeData: financePayload,
        },
        {
          userId: authUser?.id,
          username: profile?.username,
        },
      );
      try {
        await insertAccountCreationChangeLog({
          submissionId: row.id,
          changedBy: actorLabel(),
          changeType: 'verified',
          summary: editing ? 'Verified with edits' : 'Verified',
          beforeJson: before,
          afterJson: after,
        });
      } catch {
        /* non-blocking */
      }
      await load();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setReviewBusy(false);
    }
  };

  const openRejectModal = () => {
    setVerifyError(null);
    setReviewAction('rejected');
  };

  const handleApprove = async () => {
    if (!row) return;
    setVerifyError(null);
    if (!companyName.trim()) {
      setVerifyError('Company name is required.');
      return;
    }
    if (!formDraft.finance_contact_name.trim()) {
      setVerifyError('Finance contact name is required.');
      return;
    }
    if (!formDraft.finance_contact_no.trim()) {
      setVerifyError('Finance contact number is required.');
      return;
    }
    if (!formDraft.finance_email.trim()) {
      setVerifyError('Finance email is required.');
      return;
    }
    if (!finance.payment_terms) {
      setVerifyError('Payment terms is required.');
      return;
    }
    const code = customerCode.trim() || row.customer_code?.trim() || '';
    if (!code) {
      setVerifyError('Customer code is required.');
      return;
    }

    setReviewBusy(true);
    try {
      const before = {
        company_name: row.company_name,
        form_data: row.form_data,
        finance_data: row.finance_data ?? null,
        customer_code: row.customer_code ?? null,
      };
      const financePayload = {
        ...finance,
        division_code: '10',
        salesperson_code:
          formDraft.salesperson_code?.trim() || finance.salesperson_code.trim(),
      };
      const after = {
        company_name: companyName.trim(),
        form_data: formDraft,
        finance_data: financePayload,
        customer_code: code,
      };
      await updateSubmissionStatus(
        row.id,
        'approved',
        {
          customerCode: code,
          companyName: companyName.trim(),
          formData: formDraft,
          financeData: financePayload,
        },
        {
          userId: authUser?.id,
          username: profile?.username,
        },
      );
      const edited =
        JSON.stringify(before.company_name) !== JSON.stringify(after.company_name) ||
        JSON.stringify(before.form_data) !== JSON.stringify(after.form_data) ||
        JSON.stringify(before.finance_data) !== JSON.stringify(after.finance_data) ||
        JSON.stringify(before.customer_code) !== JSON.stringify(after.customer_code);
      try {
        await insertAccountCreationChangeLog({
          submissionId: row.id,
          changedBy: actorLabel(),
          changeType: 'approved',
          summary: edited || editing ? 'Approved with edits' : 'Approved by AR',
          beforeJson: before,
          afterJson: after,
        });
      } catch {
        /* non-blocking */
      }
      await load();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Approval failed.');
    } finally {
      setReviewBusy(false);
    }
  };

  const resetApplicantDraft = () => {
    if (!row) return;
    const form = row.form_data as AccountCreationFormData;
    setCompanyName(row.company_name);
    setFormDraft({ ...EMPTY_ACCOUNT_CREATION_FORM, ...form });
    setCustomerCode(row.customer_code?.trim() || '');
    setFinance({
      ...DEFAULT_ACCOUNT_CREATION_FINANCE,
      ...row.finance_data,
      salesperson_code:
        row.finance_data?.salesperson_code?.trim() || form.salesperson_code?.trim() || '',
      cut_off_time: row.finance_data?.cut_off_time?.trim() || '12:00 PM',
      division_code: '10',
      session: row.finance_data?.session?.trim() || 'AM',
    });
    setEditing(false);
    setFormKey((k) => k + 1);
  };

  if (loading || permissionsLoading) {
    return (
      <div className="af-applications-page af-form-page cff-page--floating">
        <p className="page-lead">Loading application…</p>
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="af-applications-page af-form-page cff-page--floating">
        <p className="alert alert-error">{error ?? 'Application not found.'}</p>
        <Link to={listPath} className="btn-secondary btn-sm">
          Back to list
        </Link>
      </div>
    );
  }

  const formData = row.form_data as AccountCreationFormData;

  return (
    <div className="af-applications-page af-form-page cff-page--floating">
      {verifyError ? (
        <ErrorOverlay message={verifyError} onClose={() => setVerifyError(null)} />
      ) : null}
      <div className="dem-floating-page-actions dem-blanket-detail__toolbar">
        <div className="dem-blanket-detail__toolbar-start">
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
          <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
          <span className={`af-source-badge af-source-badge--${row.source}`}>
            {row.source === 'public' ? 'Customer' : 'Internal'}
          </span>
          <span className="af-detail-toolbar__meta">
            {row.application_no} · {row.company_name}
          </span>
        </div>
        <div className="dem-floating-page-actions__btns">
          {canEditApplicant ? (
            <>
              {editing ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={reviewBusy}
                  onClick={resetApplicantDraft}
                >
                  Cancel edit
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={reviewBusy}
                  onClick={() => setEditing(true)}
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={reviewBusy}
                onClick={openRejectModal}
              >
                Reject
              </button>
              {verifying ? (
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={reviewBusy}
                  onClick={() => void handleConfirmVerification()}
                >
                  {reviewBusy ? 'Saving…' : 'Confirm verification'}
                </button>
              ) : null}
              {approving ? (
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={reviewBusy}
                  onClick={() => void handleApprove()}
                >
                  {reviewBusy ? 'Saving…' : 'Approve'}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {row.status === 'rejected' && row.review_notes?.trim() ? (
        <p className="alert alert-error" style={{ marginBottom: '0.85rem' }}>
          Rejection reason: {row.review_notes}
        </p>
      ) : null}

      <div className="af-detail-layout">
        <div className="af-detail-layout__main">
          <div className="card af-verify-panel cff-form-card">
            {verifying ? (
              <p className="af-settings-field__hint" style={{ marginBottom: 12 }}>
                {editing
                  ? 'Applicant fields are editable. Official-use stays available below.'
                  : 'Applicant fields are locked (gray). Click Edit to unlock them. Official-use fields can be filled anytime.'}
              </p>
            ) : null}
            {approving ? (
              <p className="af-settings-field__hint" style={{ marginBottom: 12 }}>
                {editing
                  ? 'Full edit is on. Payment terms and finance contact stay required for Approve.'
                  : 'Only Payment terms and Finance contact (name / no. / email) are editable. Click Edit to change other fields.'}
              </p>
            ) : null}

            <AccountCreationForm
              key={`${row.id}-${canEditApplicant ? `review-${formKey}` : 'view'}-${editing ? 'edit' : 'lock'}`}
              initialCompanyName={companyName || row.company_name}
              initialFormData={formDraft}
              requireDeclaration={false}
              requireSalespersonCode={row.source === 'internal'}
              hideSubmit
              readOnly={!canEditApplicant || !editing}
              financeContactEditable={approving && !editing}
              requireFinanceContact={approving}
              uploadSource={canEditApplicant && editing ? row.source : undefined}
              busy={reviewBusy}
              onValuesChange={(name, data) => {
                if (approving) {
                  setFormDraft(data);
                  if (editing) setCompanyName(name);
                  return;
                }
                if (!verifying || !editing) return;
                setCompanyName(name);
                setFormDraft(data);
              }}
              onSubmit={async () => undefined}
            />

            <div
              className={`af-official-use${
                verifying || (approving && editing)
                  ? ''
                  : approving
                    ? ' af-official-use--ar-partial'
                    : ' af-official-use--readonly'
              }`}
            >
              <section className="af-form__section af-official-use__block">
                <h3 className="af-form__section-title">For official use only</h3>
                <div className="af-form__grid af-form__grid--cols-2">
                  <Field
                    label="Payment terms"
                    required={approving}
                    full
                    className="af-official-use__editable"
                  >
                    <select
                      value={finance.payment_terms}
                      onChange={(e) =>
                        patchFinance(
                          'payment_terms',
                          e.target.value as AccountCreationFinanceData['payment_terms'],
                        )
                      }
                      disabled={reviewBusy || !(verifying || approving)}
                    >
                      <option value="">Select payment terms</option>
                      {(fieldOptions.payment_terms.length
                        ? fieldOptions.payment_terms
                        : PAYMENT_TERMS_OPTIONS
                      ).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Customer code" required={verifying}>
                    <input
                      type="text"
                      value={customerCode}
                      onChange={(e) => setCustomerCode(e.target.value)}
                      placeholder="e.g. F0123-A01"
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                  <Field label="Customer price group" required={verifying}>
                    <FinanceSelect
                      value={finance.customer_price_group}
                      options={
                        fieldOptions.customer_price_group.length
                          ? fieldOptions.customer_price_group
                          : CUSTOMER_PRICE_GROUP_OPTIONS
                      }
                      placeholder="Select price group"
                      onChange={(value) => patchFinance('customer_price_group', value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                  <Field label="Business group code" required={verifying}>
                    <FinanceSelect
                      value={finance.businessgroup_code}
                      options={fieldOptions.businessgroup_code}
                      placeholder="Select business group"
                      onChange={(value) => patchFinance('businessgroup_code', value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                  <Field label="Division code" required={verifying}>
                    <input type="text" value="10" readOnly />
                  </Field>
                  <Field label="Remarks" full>
                    <textarea
                      className="af-review-modal__textarea"
                      rows={2}
                      value={finance.remarks}
                      onChange={(e) => patchFinance('remarks', e.target.value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                </div>
              </section>

              <section className="af-form__section af-official-use__block">
                <h3 className="af-form__section-title">Delivery information</h3>
                {verifying && routeHint ? (
                  <p
                    className="af-settings-field__hint"
                    style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}
                  >
                    {routeHint}
                  </p>
                ) : null}
                <div className="af-form__grid af-form__grid--cols-2">
                  <Field label="Delivery schedule" required={verifying}>
                    <input
                      type="text"
                      value={finance.delivery_schedule}
                      onChange={(e) => patchFinance('delivery_schedule', e.target.value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                  <Field label="Cut off time" required={verifying}>
                    <input
                      type="text"
                      value={finance.cut_off_time}
                      onChange={(e) => patchFinance('cut_off_time', e.target.value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                  <Field label="Session" required={verifying}>
                    <select
                      value={finance.session}
                      onChange={(e) => patchFinance('session', e.target.value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    >
                      <option value="">Select session</option>
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </Field>
                  <Field label="Territory code" required={verifying}>
                    <input
                      type="text"
                      value={finance.territory_code}
                      onChange={(e) => patchFinance('territory_code', e.target.value)}
                      disabled={reviewBusy || !(verifying || (approving && editing))}
                    />
                  </Field>
                </div>
              </section>
            </div>

          </div>
        </div>

        <div className="af-detail-sidebar">
          <aside className="af-change-log card" aria-label="Change log">
            <h3 className="af-change-log__title">Change log</h3>
            <ul className="af-change-log__list">
              <li className="af-change-log__item">
                <span className="af-change-log__when">{formatSubmittedAt(row.submitted_at)}</span>
                <strong className="af-change-log__summary">Submitted</strong>
                <span className="af-change-log__by">{formatSubmittedBy(row, unifiedUsers)}</span>
              </li>
              {changeLogs.map((entry) => (
                <li key={entry.id} className="af-change-log__item">
                  <span className="af-change-log__when">{formatSubmittedAt(entry.changed_at)}</span>
                  <strong className="af-change-log__summary">
                    {entry.summary || entry.change_type}
                  </strong>
                  {entry.changed_by ? (
                    <span className="af-change-log__by">{entry.changed_by}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </aside>

          <aside className="af-attachments-panel card" aria-label="ACRA attachments">
            <h3 className="af-attachments-panel__title">ACRA Business Profile</h3>
            <AttachmentLinks
              items={normalizeAcraAttachments(formDraft)}
              emptyLabel="No ACRA files uploaded"
              onError={setVerifyError}
            />
          </aside>

          <aside className="af-attachments-panel card" aria-label="Supporting documents">
            <h3 className="af-attachments-panel__title">Supporting Documents</h3>
            <AttachmentLinks
              items={normalizeSupportingAttachments(formDraft)}
              emptyLabel="No supporting documents uploaded"
              onError={setVerifyError}
            />
          </aside>
        </div>
      </div>

      <AccountCreationReviewModal
        open={reviewAction !== null}
        action={reviewAction}
        applicationNo={row.application_no}
        initialCompanyName={row.company_name}
        initialFormData={formData}
        initialCustomerCode={row.customer_code ?? ''}
        initialFinanceData={row.finance_data}
        initialSalespersonCode={
          formData.salesperson_code ?? row.finance_data?.salesperson_code
        }
        deliveryPostal={formData.delivery_postal}
        deliveryAddress={{
          outlet_name: formData.outlet_name,
          street: formData.delivery_street,
          building: formData.delivery_building,
          unit: formData.delivery_unit,
          postal: formData.delivery_postal,
        }}
        busy={reviewBusy}
        onClose={() => {
          if (!reviewBusy) setReviewAction(null);
        }}
        onConfirm={(input) => void confirmReview(input)}
      />
    </div>
  );
}
